// #1579 — required delivery-address prompt on the China Russia-rate
// no-snapshot path. Replaces the old plain ConfirmModal: the manager MUST
// supply a delivery address before a NEW Russia snapshot is created, so the
// row never lands on the placeholder endAddress (which the SEND gate now
// blocks). Uses EditorModal so the typed-but-unsubmitted address is guarded
// against an accidental backdrop / Escape close.

import { useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Button, EditorModal, FormRow, Input, useEditorGuard } from '../../../ui/index.js';

interface RussiaRateCreateFormValues {
  endAddress: string;
}

export interface RussiaRateCreateModalProps {
  /** Fires with the trimmed, non-empty delivery address on confirm. */
  onConfirm: (endAddress: string) => void;
  onClose: () => void;
  busy: boolean;
}

export function RussiaRateCreateModal({ onConfirm, onClose, busy }: RussiaRateCreateModalProps) {
  const { t } = useTranslation(['requests', 'common']);

  // WHY (#1579): mirror the shared `endAddress` bounds (min 1 / max 2000,
  //   trimmed) but source the required-error copy from the i18n layer so the
  //   field error is localized; the trim runs before `.min(1)` so a
  //   whitespace-only entry is rejected, matching the SEND-gate rule.
  const schema = useMemo(
    () =>
      z.object({
        endAddress: z
          .string()
          .trim()
          .min(1, t('requests:actions.russiaRate.create.addressRequired'))
          .max(2000),
      }),
    [t],
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm<RussiaRateCreateFormValues>({
    resolver: zodResolver(schema),
    defaultValues: { endAddress: '' },
  });

  const onSubmit = (values: RussiaRateCreateFormValues) => {
    onConfirm(values.endAddress.trim());
  };

  return (
    <EditorModal
      open={true}
      title={t('requests:actions.russiaRate.create.title')}
      width={460}
      dirty={isDirty}
      busy={busy}
      onConfirmClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <FormRow
          label={t('requests:actions.russiaRate.create.addressLabel')}
          error={errors.endAddress?.message}
        >
          <Controller
            control={control}
            name="endAddress"
            render={({ field }) => (
              <Input
                value={field.value}
                onChange={field.onChange}
                invalid={!!errors.endAddress}
                maxLength={2000}
              />
            )}
          />
        </FormRow>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
          <CancelButton onClose={onClose} label={t('requests:actions.russiaRate.create.cancel')} />
          <Button type="submit" kind="primary" disabled={busy}>
            {t('requests:actions.russiaRate.create.confirm')}
          </Button>
        </div>
      </form>
    </EditorModal>
  );
}

// WHY: Cancel is an in-form close affordance, not a Modal-chrome close path
// (X / Esc / backdrop) — those are handled by EditorModal internally. Route
// Cancel through `confirmDestructive` so a dirty form still prompts before
// discarding the typed address.
function CancelButton({ onClose, label }: { onClose: () => void; label: string }) {
  const { confirmDestructive } = useEditorGuard();
  return (
    <Button kind="ghost" onClick={() => confirmDestructive(onClose)}>
      {label}
    </Button>
  );
}
